// src/context/SubscriptionContext.jsx
// Global subscription and seats state management

import React, { createContext, useContext, useState, useEffect, useCallback } from "react";
import { billingService } from "../api/billingService";

const SubscriptionContext = createContext(null);

/**
 * Subscription Provider - Manages global subscription and seats state
 *
 * Provides:
 * - status: Full subscription status object from API
 * - seats: Seat information for the organization
 * - loading: Whether data is being fetched
 * - error: Any error that occurred
 * - refresh: Function to manually refresh all data
 * - refreshSeats: Function to refresh only seats data
 *
 * Status object shape:
 * {
 *   has_subscription: boolean,
 *   subscription_status: "active" | "past_due" | "canceled" | null,
 *   subscription_plan: "starter" | "team" | "growth" | null,
 *   trial_ends_at: string | null,
 *   trial_active: boolean,
 *   access_granted: boolean
 * }
 *
 * Seats object shape:
 * {
 *   organization_id: number,
 *   organization_name: string,
 *   subscription_plan: string | null,
 *   seat_limit: number,
 *   seats_used: number,
 *   seats_available: number,
 *   can_add_members: boolean
 * }
 */
export function SubscriptionProvider({ children }) {
  const [status, setStatus] = useState(null);
  const [seats, setSeats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const refreshStatus = useCallback(async () => {
    try {
      const data = await billingService.getStatus();
      setStatus(data);
      return data;
    } catch (err) {
      console.error("Failed to fetch subscription status:", err);
      // Fail open - don't block users if API is temporarily down
      const fallback = {
        access_granted: true,
        trial_active: false,
        has_subscription: false,
        subscription_status: null,
        subscription_plan: null,
        trial_ends_at: null,
      };
      setStatus(fallback);
      return fallback;
    }
  }, []);

  const refreshSeats = useCallback(async () => {
    try {
      const data = await billingService.getSeats();
      setSeats(data);
      return data;
    } catch (err) {
      console.error("Failed to fetch seats:", err);
      // Fail open with default values
      const fallback = {
        organization_id: null,
        organization_name: "My Organization",
        subscription_plan: null,
        seat_limit: 1,
        seats_used: 1,
        seats_available: 0,
        can_add_members: false,
      };
      setSeats(fallback);
      return fallback;
    }
  }, []);

  const refresh = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      await Promise.all([refreshStatus(), refreshSeats()]);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [refreshStatus, refreshSeats]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const value = {
    status,
    seats,
    loading,
    error,
    refresh,
    refreshSeats,
  };

  return (
    <SubscriptionContext.Provider value={value}>
      {children}
    </SubscriptionContext.Provider>
  );
}

/**
 * Hook to access subscription context
 * @returns {{
 *   status: Object | null,
 *   seats: Object | null,
 *   loading: boolean,
 *   error: string | null,
 *   refresh: () => Promise<void>,
 *   refreshSeats: () => Promise<void>
 * }}
 */
export function useSubscription() {
  const context = useContext(SubscriptionContext);

  if (context === null) {
    throw new Error("useSubscription must be used within a SubscriptionProvider");
  }

  return context;
}

/**
 * Helper hook to get trial days remaining
 * @returns {number | null} Days remaining in trial, or null if not in trial
 */
export function useTrialDaysRemaining() {
  const { status } = useSubscription();

  if (!status?.trial_active || !status?.trial_ends_at) {
    return null;
  }

  const trialEnd = new Date(status.trial_ends_at);
  const now = new Date();
  const daysRemaining = Math.ceil((trialEnd - now) / (1000 * 60 * 60 * 24));

  return Math.max(0, daysRemaining);
}

/**
 * Helper hook to check if user can add team members
 * @returns {boolean}
 */
export function useCanAddMembers() {
  const { seats } = useSubscription();
  return seats?.can_add_members ?? false;
}

/**
 * Helper hook to get seats usage percentage
 * @returns {number} Percentage (0-100)
 */
export function useSeatsUsagePercent() {
  const { seats } = useSubscription();
  if (!seats || seats.seat_limit === 0) return 0;
  return Math.round((seats.seats_used / seats.seat_limit) * 100);
}
